import { formatBillingCurrency } from '../../utils/billingRound';
import type { AzureMonthlyTotal, AzureServiceCostDelta } from '../../types/azureCost';
import type { MsrObservationEntry } from './msrReportTypes';

function monthYearShortLabel(year: number, month: number): string {
  return new Date(Date.UTC(year, month - 1, 1)).toLocaleString('en-US', {
    month: 'short',
    year: 'numeric',
    timeZone: 'UTC',
  });
}

function formatServiceCostDeltaLine(
  serviceName: string,
  direction: 'increased' | 'decreased',
  amount: number,
  currency: string
): string {
  return `"${serviceName}" cost ${direction} by ${formatBillingCurrency(amount, currency)}.`;
}

export function buildAzureCostObservations(
  last3Months: AzureMonthlyTotal[],
  serviceCostDeltas: AzureServiceCostDelta[],
  currency: string
): MsrObservationEntry[] {
  if (last3Months.length < 2) return [];

  const previous = last3Months[last3Months.length - 2]!;
  const current = last3Months[last3Months.length - 1]!;
  const change = current.totalCost - previous.totalCost;
  if (change === 0 && serviceCostDeltas.length === 0) return [];

  const direction = change >= 0 ? 'increased' : 'decreased';
  const observations: MsrObservationEntry[] = [
    `Total Accumulated cost for the month of ${monthYearShortLabel(current.year, current.month)} has been ${direction} by ${formatBillingCurrency(Math.abs(change), currency)} from ${formatBillingCurrency(previous.totalCost, currency)} to ${formatBillingCurrency(current.totalCost, currency)}.`,
  ];

  if (serviceCostDeltas.length > 0) {
    const increased = serviceCostDeltas
      .filter((d) => d.delta > 0)
      .sort((a, b) => b.delta - a.delta);
    const decreased = serviceCostDeltas
      .filter((d) => d.delta < 0)
      .sort((a, b) => a.delta - b.delta);

    const children: string[] = [
      ...increased.map((row) =>
        formatServiceCostDeltaLine(row.serviceName, 'increased', row.delta, currency)
      ),
      ...decreased.map((row) =>
        formatServiceCostDeltaLine(
          row.serviceName,
          'decreased',
          Math.abs(row.delta),
          currency
        )
      ),
    ];

    observations.push({
      text: 'Below are the resource wise utilization details:',
      children,
    });
  }

  observations.push('Detailed cost analysis has been given in the upcoming slide.');
  return observations;
}
